import React from "react";
import { useAuthStore } from "stores/AuthStoreProvider";
import { ChatStore } from "./ChatStore";
import { ChatStoreContext } from "./chatStoreContext";

/**
 * Mounted inside the authenticated tree only: it needs the signed-in user.
 * Owns the store's lifetime: loads data and opens the realtime socket on
 * mount, closes it on unmount (sign-out).
 */
export function ChatStoreProvider({ children }: React.PropsWithChildren) {
  const authStore = useAuthStore();
  const userID = authStore.user?.data.id;
  const store = React.useMemo(
    () =>
      userID
        ? new ChatStore(userID, async () => {
            // The ticket call found no session: re-check it, and keep
            // retrying only while the user is still signed in.
            await authStore.revalidateSession();
            return authStore.isAuthenticated;
          })
        : null,
    [userID, authStore],
  );

  React.useEffect(() => {
    if (!store) return;
    void store.init();
    return () => store.dispose();
  }, [store]);

  if (!store) return null;
  return (
    <ChatStoreContext.Provider value={store}>
      {children}
    </ChatStoreContext.Provider>
  );
}
